import React, { useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { USER_TYPES } from '@medflex/utils/constants';
import { TRACKING_ACTIONS, trackCustomEvent, trackEvent } from '@medflex/utils/Tracking';
import LoadingView from '@medflex/components/stories/LoadingView';
import PhysicianChatContactList from '@medflex/components/stories/PhysicianChatContactList';
import isSelf from '@medflex/utils/isSelf';
import isSupportPatient from '@medflex/utils/isSupportPatient';
import SelfAvatar from '@medflex/components/stories/SelfAvatar';
import { localStorage } from '@medflex/utils/browserStorage';
import useChatContacts from '../hooks/useChatContacts';
import useOnlineStatus from '../hooks/useOnlineStatus';
import usePermissions from '../hooks/usePermissions';
import useUserProfile from '../hooks/useUserProfile';
import useClipboard from '../hooks/useClipboard';
import useModal from '../hooks/useModal';
import getParentName from '../../utils/getParentName';
import useLocationSearchParams from '../../hooks/useLocationSearchParams';
import useSessionChats from '../hooks/useSessionChats';
import useAddressBookContacts from '../hooks/useAddressBookContacts';
import getSubtitle from '../../utils/getSubtitle';
import mapSessionChatToContact from '../../utils/mapSessionChatToContact';
import useExternalServices, { EXTERNAL_SERVICES } from '../hooks/useExternalServices';
import { MODALS } from '../hooks/useIsModal';

const LS_KEY = 'chatFilter';
const LS = localStorage('chats');

const sortActiveContacts = (a, b) => {
  /* eslint-disable no-underscore-dangle */
  const aDateSort = a.messages.lastAt ? a._dateSort : new Date().getTime();
  const bDateSort = b.messages.lastAt ? b._dateSort : new Date().getTime();
  if (aDateSort < bDateSort) return 1;
  if (aDateSort > bDateSort) return -1;
  if (a._nameSort < b._nameSort) return -1;
  if (a._nameSort > b._nameSort) return 1;
  /* eslint-enable no-underscore-dangle */
  return 0;
};

const sortDefaultContacts = (a, b) => {
  if (a.isSupport) return -1;
  if (b.isSupport) return 1;
  if (a.isTeamChat) return 1;
  if (b.isTeamChat) return -1;
  return 0;
};

const ChatListModule = ({ selectedChatId }) => {
  const { t } = useTranslation('global');
  const { contacts, isLoadingContacts, toggleArchive } = useChatContacts();
  const { chats: sessionChats, loadingStatus: sessionChatsLoadingStatus, load } = useSessionChats();
  const { hasPatientAccess, hasPhysicianAccess } = usePermissions();
  const history = useHistory();
  const location = useLocation();
  const { userProfile } = useUserProfile();
  const { contactsWithDrafts } = useClipboard();
  const { onlineUserIds } = useOnlineStatus();
  const { filterContacts } = useLocationSearchParams();
  const params = new URLSearchParams(location.search);
  const { open: openModal } = useModal();
  const { staff } = useAddressBookContacts();
  const [simpleprax] = useExternalServices([EXTERNAL_SERVICES.SIMPLEPRAX]);

  useEffect(() => {
    if (sessionChatsLoadingStatus === 'idle') {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionChatsLoadingStatus]);

  const filterContactsLS = LS.get(LS_KEY);
  const filter = filterContacts || filterContactsLS || 'all';

  const handleViewContactInfos = contactUserId =>
    openModal({ modal: MODALS.contact_info, userId: contactUserId });

  const handleStartQuestionnaire = chatId => {
    const { user } = contacts.find(({ id }) => id === chatId);
    openModal({ modal: MODALS.NEW_QUESTIONNAIRE, userId: user.id });
  };

  const handleCreateSubcontact = contactId => openModal({ modal: 'createSubcontact', contactId });

  const handleSharePatientContact = (event, contactUserId) => {
    trackEvent(event, { action: TRACKING_ACTIONS.OPEN });
    openModal({ modal: MODALS.sharePatientContact, filter: 'physicians', contactUserId });
  };

  const handleClickCompose = () => openModal({ modal: 'newMessage' });

  const handleArchiveToggled = async contact => {
    await toggleArchive(contact);
    if (!contact.archivedAt) {
      history.push('/chat');
    }
  };

  const normalizeContacts = useMemo(
    () =>
      contacts.reduce((acc, contact) => {
        const isSelfContact = isSelf(contact.user);
        const isSupport = isSupportPatient(contact.user);
        return [
          ...acc,
          {
            ...contact,
            parentName: getParentName(contact.mainContactId, contacts),
            canCreateSubcontact:
              contact.type === USER_TYPES.PATIENT && !contact.mainContactId && contact.enabled,
            canSharePatientContact:
              contact.type === USER_TYPES.PATIENT &&
              !contact.mainContactId &&
              contact.enabled &&
              hasPatientAccess &&
              hasPhysicianAccess,
            isSupport,
            isOnline: onlineUserIds.includes(contact.user.id),
            isSelfContact,
            user: {
              ...contact.user,
              subtitle: getSubtitle(contact, userProfile, t),
              avatar: isSelfContact ? <SelfAvatar /> : contact.user.avatar,
              initials: isSelfContact ? null : contact.user.initials,
              fullNameWithTitle: isSelfContact
                ? t('physician:address_book.self_title')
                : contact.user.fullNameWithTitle,
            },
          },
        ];
      }, []),
    [contacts, hasPatientAccess, hasPhysicianAccess, onlineUserIds, t, userProfile],
  );

  const filteredContacts = useMemo(
    () =>
      normalizeContacts.filter(contact => {
        if (filter === 'important' && !contact.messagesMarkedAsImportant) return null;
        if (filter === 'unread' && !contact.messages.unread) return null;
        const isSelfContact = isSelf(contact.user);
        const isSupport = isSupportPatient(contact.user);
        if (!(isSelfContact || isSupport)) {
          if (
            !contact.messages.lastAt &&
            !contactsWithDrafts.includes(contact.id) &&
            !(contact.id === selectedChatId)
          ) {
            return null;
          }
          if (contact.archivedAt && !(contact.id === selectedChatId)) return null;
        }
        return contact;
      }),
    [contactsWithDrafts, filter, normalizeContacts, selectedChatId],
  );

  const normalizedSessionChats = useMemo(
    () =>
      sessionChats.map(
        mapSessionChatToContact({
          userId: userProfile.id,
          teamSubTitle: t('messenger:team_chat_subtitle'),
        }),
      ),
    [sessionChats, userProfile, t],
  );

  const filteredSessionChats = useMemo(
    () =>
      normalizedSessionChats.filter(s => {
        if (filter === 'important' && !s.messagesMarkedAsImportant) return null;
        if (filter === 'unread' && !s.messages.unread) return null;
        if (selectedChatId && selectedChatId === s.id) return s;
        if (!s.messages.lastAt && !s.isTeamChat) return null;
        return s;
      }),
    [normalizedSessionChats, filter, selectedChatId],
  );

  const handleOpenMainContact = mainContactId => {
    history.push(`/chat/${mainContactId}`);
  };

  const allContacts = useMemo(() => {
    if (
      !isLoadingContacts &&
      sessionChatsLoadingStatus !== 'idle' &&
      (normalizeContacts || normalizedSessionChats)
    ) {
      return [...normalizeContacts, ...normalizedSessionChats];
    }

    return [];
  }, [isLoadingContacts, sessionChatsLoadingStatus, normalizeContacts, normalizedSessionChats]);

  const amounts = useMemo(() => {
    const unreadCount = allContacts.filter(
      c => c.messages.unread && !c.archivedAt && c.messages.lastAt,
    );
    const importantCount = allContacts.filter(
      c => c.messagesMarkedAsImportant && !c.archivedAt && c.messages.lastAt,
    );

    return {
      unread: unreadCount.length,
      important: importantCount.length,
    };
  }, [allContacts]);

  const messageContacts = useMemo(() => {
    if (
      !isLoadingContacts &&
      sessionChatsLoadingStatus !== 'idle' &&
      (contacts || filteredSessionChats)
    ) {
      return [...filteredContacts, ...filteredSessionChats];
    }

    return [];
  }, [
    isLoadingContacts,
    sessionChatsLoadingStatus,
    contacts,
    filteredSessionChats,
    filteredContacts,
  ]);

  const handleClickContact = (event, contact) => {
    const { id } = contact;

    trackEvent(event, { actionText: '' });

    history.push(
      `/chat/${id}?${params.toString()}${
        filterContacts === 'important'
          ? `#${messageContacts.find(c => c.id === id).messagesMarkedAsImportant.slice(-1)[0]}`
          : ''
      }`,
    );
  };

  const groupedContacts = useMemo(() => {
    if (messageContacts.length) {
      const grouped = messageContacts.reduce(
        (acc, contact) => {
          // display teamChat only if team consists of at least 2 staffs or teamChat has content
          if (contact.isTeamChat && staff.length < 2 && !contact.messages.lastAt) return acc;
          // default contact type
          if (contact.isSelfContact || contact.isSupport || contact.isTeamChat) {
            return {
              ...acc,
              default: {
                ...acc.default,
                contacts: [...acc.default.contacts, contact],
              },
            };
          }
          // user contact type
          return {
            ...acc,
            users: {
              ...acc.users,
              contacts: [...acc.users.contacts, contact],
            },
          };
        },
        // initial values
        {
          default: { contacts: [] },
          users: {
            contacts: [],
          },
        },
      );
      return {
        default: {
          ...grouped.default,
          contacts: grouped.default.contacts.sort(sortDefaultContacts),
        },
        users: { ...grouped.users, contacts: grouped.users.contacts.sort(sortActiveContacts) },
      };
    }
    return {};
  }, [messageContacts, staff]);

  const handleChangeFilter = value => {
    if (value === 'important') {
      params.set('filterContacts', 'important');
      LS.set(LS_KEY, 'important');
    } else if (value === 'unread') {
      params.set('filterContacts', 'unread');
      LS.set(LS_KEY, 'unread');
    } else if (value === 'all') {
      params.delete('filterContacts');
      LS.remove(LS_KEY);
    }

    trackCustomEvent({
      eventCategory: 'physician_chat_list',
      eventAction: 'switched tab',
      eventName: value || 'all',
    });

    return history.push(`?${params.toString()}`);
  };

  return (
    <div
      data-component="ChatListModule"
      className="webtest-contacts flex-grow min-h-60 flex flex-col xl:max-w-96 xl:min-w-96 w-full xl:w-auto"
    >
      {!contacts || isLoadingContacts ? (
        <LoadingView
          title={t('address_book.loading.title')}
          message={t('address_book.loading.message')}
        />
      ) : (
        <PhysicianChatContactList
          onClickContact={handleClickContact}
          contacts={groupedContacts}
          selectedContactId={selectedChatId}
          onViewContactInfos={handleViewContactInfos}
          onCreateSubcontact={handleCreateSubcontact}
          onSharePatientContact={handleSharePatientContact}
          onClickCompose={handleClickCompose}
          onFilterChanged={handleChangeFilter}
          onOpenMainContact={handleOpenMainContact}
          onToggleArchive={handleArchiveToggled}
          onClickSubtitleShowMore={handleViewContactInfos}
          onStartQuestionnaire={handleStartQuestionnaire}
          hasQuestionnaireFeatureAccess={simpleprax?.active}
          filter={filter}
          count={amounts}
        />
      )}
    </div>
  );
};

ChatListModule.propTypes = {
  selectedChatId: PropTypes.string,
};

export default ChatListModule;
